import {
  Backdrop,
  Box,
  Card, CardContent, CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Input, List, ListItem,
  ListItemText,
  Paper, Select,
  Tooltip,
  Typography
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ImageSearchIcon from '@material-ui/icons/ImageSearch';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import axios from 'src/utils/axios';
import { getUser, sc } from 'src/utils/Common';
import { setSatisAdet } from 'src/utils/storage/NavBarAdet';
import { text } from '../../modules/languages/index';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0)
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default
  },
  searchInput: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    fontSize: '0.9rem'
  },
  daySelect: {
    marginLeft: theme.spacing(2),
    minWidth: 100,
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  timeBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 70,
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    borderRight: `3px solid ${theme.palette.primary.main}`,
    marginRight: theme.spacing(2)
  },
  customerTitle: {
    fontWeight: 700,
    cursor: 'pointer',
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline'
    }
  },
  materialBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: '#f9f9f9',
    border: '1px solid #eee'
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.7)'
  }
}));

function SatisTodos({ className, ...rest }) {
  const classes = useStyles();
  const history = useHistory();
  const [satistodos, setSatisTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState("Bugün");

  const sessionUser = getUser();
  const birimKodu = sessionUser.ServiceShopId;
  const teknisyenKodu = sessionUser.TechnicianId;

  useEffect(() => {
    let mounted = true;
    const fetchTodos = async () => {
      try {
        const response = await axios.get(`/GetArtemisSatisAksesuar/${birimKodu}/${teknisyenKodu}`);
        if (mounted) {
          const data = response.data || [];
          setSatisTodos(data);
          setSatisAdet(data.length);
          setLoading(false);
        }
      } catch (error) {
        logger.error("Veri yükleme hatası:", error);
        setLoading(false);
      }
    };
    fetchTodos();
    return () => { mounted = false; };
  }, [birimKodu, teknisyenKodu]);

  // Gelişmiş Filtreleme (useMemo ile optimize edildi)
  const filteredData = useMemo(() => {
    return satistodos
      .filter(item => item.GUN === selectedDay)
      .filter(item => {
        const materials = JSON.parse(item.MALZEMELER || "[]");
        const matchSearch = sc(item.FisNo, searchTerm) || 
                           sc(item.Musteri, searchTerm) ||
                           materials.some(m => sc(m.MALZEME_STOK_NO, searchTerm) || sc(m.ACIKLAMA, searchTerm));
        return matchSearch;
      })
      .sort((a, b) => a.SIRA - b.SIRA);
  }, [satistodos, searchTerm, selectedDay]);

  const handleShowMaterial = (code) => {
    const newWindow = window.open("", "_blank");
    axios.post('/FileImportExport/ArtemisMalzeme', 
      { MALZEME_STOK_NO: code }, 
      { responseType: 'blob', headers: { 'Content-Type': 'application/json' } }
    ).then(res => {
      newWindow.location.href = URL.createObjectURL(res.data);
    }).catch(() => newWindow.close());
  };

  if (!window.navigator.onLine) return null;

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Card elevation={3} style={{ borderRadius: 16 }}>
        <Box p={2}>
          <Paper className={classes.searchContainer} elevation={0}>
            <SearchIcon color="action" />
            <Input
              className={classes.searchInput}
              disableUnderline
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={text("Arama_Yap") || "Müşteri veya malzeme ara..."}
            />
            <Divider orientation="vertical" flexItem style={{ margin: '0 12px' }} />
            <CalendarTodayIcon fontSize="small" color="disabled" />
            <Select
              native
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              disableUnderline
              className={classes.daySelect}
            >
              <option value="Bugün">Bugün</option>
              <option value="Yarın">Yarın</option>
            </Select>
          </Paper>
        </Box>

        <CardHeader 
          title={<Typography variant="h6" style={{ fontWeight: 800 }}>Satış Fırsatları</Typography>} 
          subheader={`${filteredData.length} fırsat bulundu`}
        />
        <Divider />

        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        <CardContent style={{ padding: 0 }}>
          <List>
            {filteredData.map((log, logIndex) => (
              <React.Fragment key={`${log.FisNo}-${logIndex}`}>
                <ListItem alignItems="flex-start" style={{ padding: '16px 24px' }}>
                  <Box className={classes.timeBadge}>
                    <Typography variant="h6" color="primary" style={{ lineHeight: 1 }}>{log.TIME_SLOT1}</Typography>
                    <Typography variant="caption" color="textSecondary">{log.TIME_SLOT2}</Typography>
                    <Typography variant="overline" style={{ fontWeight: 'bold', marginTop: 4 }}>{log.GUN}</Typography>
                  </Box>

                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Typography 
                        component="div"
                        className={classes.customerTitle}
                        onClick={() => history.push(`/WorkOrder/${log.FisNo}/summary`)}
                      >
                        {log.Musteri} <small style={{ color: '#999', fontWeight: 400 }}>#{log.FisNo}</small>
                      </Typography>
                    }
                    secondary={
                      <Box mt={1}>
                        {JSON.parse(log.MALZEMELER).map((m, i) => (
                          <Box key={i} className={classes.materialBox}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" component="span" color="textPrimary">
                                <strong>{m.KUL_MODEL_KODU}</strong> | {m.MODEL}
                              </Typography>
                              <Tooltip title="Görsel Ara">
                                <IconButton size="small" onClick={() => handleShowMaterial(m.MALZEME_STOK_NO)}>
                                  <ImageSearchIcon color="primary" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography variant="caption" component="span" color="textSecondary" display="block">
                              {i + 1}. {m.MALZEME_STOK_NO} - {m.ACIKLAMA}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    }
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
            {!loading && filteredData.length === 0 && (
              <Box p={5} textAlign="center">
                <Typography color="textSecondary">Seçili kriterlere uygun satış fırsatı bulunamadı.</Typography>
              </Box>
            )}
          </List>
        </CardContent>
      </Card>
    </div>
  );
}

SatisTodos.propTypes = { className: PropTypes.string };

export default SatisTodos;